import { useState, useEffect } from 'react';
import { Copy, Check, ExternalLink, Pencil, Globe, Sparkles } from 'lucide-react';
import { Badge } from './Badge';
import { Button } from './Button';
import { Input } from './Input';

interface DemoLinkSectionProps {
  propertyId: string;
  slug?: string;
  propertyName?: string;
  propertyWebsite?: string;
  onSlugChange?: (slug: string) => void;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function DemoLinkSection({ propertyId, slug, propertyName, propertyWebsite, onSlugChange }: DemoLinkSectionProps) {
  const [copied, setCopied] = useState(false);
  const [copiedDemo, setCopiedDemo] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSlug, setEditSlug] = useState('');
  const [showDemoSetup, setShowDemoSetup] = useState(false);

  const demoBaseUrl = import.meta.env.VITE_DEMO_BASE_URL || 'https://demo.lasoxp.com';
  const effectiveSlug = slug || (propertyName ? generateSlug(propertyName) : propertyId);
  const demoUrl = `${demoBaseUrl}/${effectiveSlug}`;

  useEffect(() => {
    setEditSlug(effectiveSlug);
  }, [effectiveSlug]);

  const handleCopyWebsite = async () => {
    if (!propertyWebsite) return;
    try {
      await navigator.clipboard.writeText(propertyWebsite);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* no-op */ }
  };

  const handleCopyDemo = async () => {
    try {
      await navigator.clipboard.writeText(demoUrl);
      setCopiedDemo(true);
      setTimeout(() => setCopiedDemo(false), 2000);
    } catch { /* no-op */ }
  };

  const handleSaveSlug = () => {
    const cleaned = editSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-|-$/g, '');
    if (cleaned && onSlugChange) {
      onSlugChange(cleaned);
    }
    setEditing(false);
  };

  const slugValidation = !editSlug || /^[a-z0-9-]+$/.test(editSlug) ? null : 'Lowercase letters, numbers, and hyphens only';

  return (
    <div className="space-y-4">
      {/* Primary: Visit your website */}
      {propertyWebsite && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Globe className="w-4 h-4 text-surface-400" />
            <span className="text-sm font-medium text-surface-700">Live Preview</span>
          </div>
          <p className="text-xs text-surface-500 mb-2">
            Your widget is live. Visit your website to see changes in real time.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-surface-50 border border-surface-200 rounded-lg px-3 py-2 text-sm font-mono text-surface-700 truncate">
              {propertyWebsite}
            </div>
            <button
              onClick={handleCopyWebsite}
              className="p-2 rounded-lg border border-surface-200 hover:bg-surface-50 text-surface-500 hover:text-surface-700 transition-colors"
              title="Copy website URL"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </button>
            <a
              href={propertyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Visit Site
            </a>
          </div>
        </div>
      )}

      {/* Secondary: Demo link for prospects */}
      <div className="border-t border-surface-100 pt-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-surface-400" />
            <span className="text-sm font-medium text-surface-700">Demo Link</span>
            <Badge variant="secondary" size="sm">For prospects</Badge>
          </div>
          {!showDemoSetup && slug && (
            <button
              onClick={() => setShowDemoSetup(true)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Edit
            </button>
          )}
          {!showDemoSetup && !slug && (
            <button
              onClick={() => setShowDemoSetup(true)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Set up
            </button>
          )}
        </div>
        <p className="text-xs text-surface-500 mb-2">
          Shareable preview page that embeds your website with the chat widget overlaid. Use before the widget is installed, or to share with prospects.
        </p>

        {showDemoSetup || slug ? (
          <>
            {editing ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-surface-500 font-mono">{demoBaseUrl}/</span>
                <Input
                  type="text"
                  value={editSlug}
                  onChange={(e) => setEditSlug(e.target.value.toLowerCase().replace(/\s/g, '-'))}
                  className="flex-1 font-mono"
                  placeholder="your-property-slug"
                  error={slugValidation ?? undefined}
                />
                <Button variant="primary" size="sm" onClick={handleSaveSlug} disabled={!!slugValidation || !editSlug}>
                  Save
                </Button>
                <Button variant="secondary" size="sm" onClick={() => { setEditing(false); setEditSlug(effectiveSlug); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-surface-50 border border-surface-200 rounded-lg px-3 py-2 text-sm font-mono text-surface-700 truncate">
                  {demoUrl}
                </div>
                {onSlugChange && (
                  <button
                    onClick={() => setEditing(true)}
                    className="p-2 rounded-lg border border-surface-200 hover:bg-surface-50 text-surface-500 hover:text-surface-700 transition-colors"
                    title="Edit slug"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleCopyDemo}
                  disabled={!slug}
                  className={`p-2 rounded-lg border border-surface-200 transition-colors ${
                    slug ? 'hover:bg-surface-50 text-surface-500 hover:text-surface-700' : 'text-surface-300 cursor-not-allowed'
                  }`}
                  title={slug ? 'Copy demo URL' : 'Save a slug first to copy the link'}
                >
                  {copiedDemo ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                {slug ? (
                  <a
                    href={demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-surface-200 text-surface-700 text-sm font-medium hover:bg-surface-50 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Demo
                  </a>
                ) : (
                  <span
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-surface-200 text-surface-400 text-sm font-medium cursor-not-allowed"
                    title="Save a slug first to enable the demo link"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Demo
                  </span>
                )}
              </div>
            )}

            {!slug && !editing && (
              <p className="text-xs text-amber-600 mt-2">
                Set a custom slug and save settings to activate this demo link.
              </p>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
